/**
 * Repay a loan.
 *
 * Opens pre-filled with the outstanding amount and the correct direction —
 * borrower → lender. That direction is not a detail: reversing it would record
 * a second loan rather than a repayment, so the fund fields are fixed and only
 * the locations are chosen.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/misc';
import { ResponsiveDialog } from '@/components/ui/dialog';
import { AmountInput } from '@/components/AmountInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatMoney, todayIso, toCanonicalAmount, formatAge } from '@/lib/format';
import { useLocations, useRepayLoan } from '@/hooks/useApi';
import type { Loan } from '@/lib/types';

export function RepayLoanDialog({
  loan,
  onClose,
}: {
  loan: Loan | null;
  onClose: () => void;
}): JSX.Element {
  const [amountRaw, setAmountRaw] = useState('');
  const [amountMinor, setAmountMinor] = useState<number | null>(null);
  const [fromLocationId, setFromLocationId] = useState<number | null>(null);
  const [toLocationId, setToLocationId] = useState<number | null>(null);
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState('');

  const { data: locations = [] } = useLocations();
  const repay = useRepayLoan();

  useEffect(() => {
    if (!loan) return;
    // Default to repaying in full — that is what "върни заема" usually means.
    setAmountRaw(toCanonicalAmount(loan.outstandingMinor, loan.decimals));
    setAmountMinor(loan.outstandingMinor);
    setDate(todayIso());
    setNote('');
    setFromLocationId(null);
    setToLocationId(null);
  }, [loan]);

  if (!loan) {
    return <ResponsiveDialog open={false} onOpenChange={onClose} title="" children={null} />;
  }

  const overpay = amountMinor != null && amountMinor > loan.outstandingMinor;
  const canSave =
    amountMinor != null && amountMinor > 0 && !overpay && fromLocationId != null && toLocationId != null;

  const handleSave = async () => {
    if (!canSave || amountMinor == null) return;
    try {
      const updated = await repay.mutateAsync({
        id: loan.id,
        date,
        amount: toCanonicalAmount(amountMinor, loan.decimals),
        from: { fundId: loan.toFundId, locationId: fromLocationId },
        to: { fundId: loan.fromFundId, locationId: toLocationId },
        note: note || undefined,
      });
      toast.success(
        updated.closedAt
          ? 'Заемът е погасен напълно и е закрит'
          : `Погасено. Остават ${formatMoney(updated.outstandingMinor, updated.currency, updated.decimals)}`,
      );
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <ResponsiveDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Погасяване на заем"
      description={`${loan.toFundName} дължи на ${loan.fromFundName} · ${formatAge(loan.ageDays)}`}
      className="sm:max-w-md"
    >
      <div className="space-y-4">
        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Първоначално</span>
            <span className="tabular">
              {formatMoney(loan.principalMinor, loan.currency, loan.decimals)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Върнато</span>
            <span className="tabular">
              {formatMoney(loan.repaidMinor, loan.currency, loan.decimals)}
            </span>
          </div>
          <div className="mt-1 flex justify-between border-t pt-1 font-medium">
            <span>Остава</span>
            <span className="tabular">
              {formatMoney(loan.outstandingMinor, loan.currency, loan.decimals)}
            </span>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="rp-amount">Сума</Label>
          <AmountInput
            id="rp-amount"
            value={amountRaw}
            onChange={setAmountRaw}
            onValueChange={setAmountMinor}
            currency={loan.currency}
            decimals={loan.decimals}
            autoFocus
          />
          {overpay ? (
            <p className="text-xs text-destructive">Сумата надвишава остатъка по заема.</p>
          ) : null}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="rp-from">Плаща се от ({loan.toFundName})</Label>
            <Select
              value={fromLocationId ? String(fromLocationId) : ''}
              onValueChange={(v) => setFromLocationId(Number(v))}
            >
              <SelectTrigger id="rp-from">
                <SelectValue placeholder="Място" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((l) => (
                  <SelectItem key={l.id} value={String(l.id)}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rp-to">Връща се в ({loan.fromFundName})</Label>
            <Select
              value={toLocationId ? String(toLocationId) : ''}
              onValueChange={(v) => setToLocationId(Number(v))}
            >
              <SelectTrigger id="rp-to">
                <SelectValue placeholder="Място" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((l) => (
                  <SelectItem key={l.id} value={String(l.id)}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="rp-date">Дата</Label>
          <Input
            id="rp-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            max={todayIso()}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="rp-note">Бележка</Label>
          <Textarea id="rp-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Откажи
          </Button>
          <Button className="flex-1" disabled={!canSave || repay.isPending} onClick={() => void handleSave()}>
            {repay.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Погаси
          </Button>
        </div>
      </div>
    </ResponsiveDialog>
  );
}
